import ApiService from "@/utils/ApiService";
import {SE_SYNC_TYPES} from "@/models/SE_SYNC_TYPES";
import {TOASTS_TYPES} from "@/models/TOASTS_TYPES";
import ToastUtils from "@/utils/ToastUtils";
import useI18nGlobal from "@/utils/i18n";
const {createToastFromStore} = ToastUtils();
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';
const {i18n} = useI18nGlobal();
const {t} = i18n.global;
import {updateEdgeRelated, preparePositionDataRequest, restoreNodePosition} from "@/services/elements/NodePositionService";

//: Debounce window for keyboard nudges. A burst of arrow-key moves within this
//: window is persisted as a single PATCH so the map history records one
//: MapChange instead of one per pixel.
const NUDGE_SAVE_DEBOUNCE = 500;

export default {
  namespaced: true,
  state: {
    savePositionTimeout: null,
    nodesPositionOrder: [],
    nudgeSaveTimeout: null,
    nudgedNodeIds: [],
  },
  getters: {
    getRawNodes(state, getters, rootState, rootGetters){
      return rootGetters['nodes/getRawNodes'];
    },
    getNodes(state, getters, rootState, rootGetters){
      return rootGetters['nodes/getNodes'];
    },
    getCurrentMap(state, getters, rootState, rootGetters) {
      return rootGetters['maps/getCurrentMap']
    },
    getMapsURL(state, getters, rootState, rootGetters){
      return rootGetters['maps/getMapsURL']
    },
    getElementsURL(state, getters, rootState, rootGetters) {
      return rootGetters['maps/getElementsURL']
    },
  },
  mutations: {
    UPDATE_NODE_ORIGINAL_GEOMETRY(state, nodeData){
      const {id, geometry, nodesOriginal} = nodeData;
      const nodeOriginal = nodesOriginal.find(node => node.id === parseInt(id));
      Object.assign(nodeOriginal.geometry, geometry);
    },
  },
  actions: {
    nodeSavePosition({state, getters, commit, dispatch}, nodeId){
      // pause saving coordinates in case of another node was being moved immediately
      if(nodeId === null) {
        clearTimeout(state.savePositionTimeout);
        return;
      }
      // add nodeId to saving data if it's not a trigger to save from edit node settings
      // otherwise that means this nodeId already included
      if(nodeId !== 'save' && !state.nodesPositionOrder.includes(nodeId)) state.nodesPositionOrder.push(nodeId);
      if(!state.nodesPositionOrder.length) return;
      // clearTimeout(state.savePositionTimeout);
      state.savePositionTimeout = setTimeout(async () => {
        const mapsURL = getters.getMapsURL;
        const elementsURL = getters.getElementsURL;
        const mapId = getters.getCurrentMap.id;
        const nodesOriginal = getters.getRawNodes;
        const nodes = getters.getNodes;

        const {data, nodesIdToSave} = preparePositionDataRequest({nodeIDs: state.nodesPositionOrder, nodes});

        if(!data.nodes.length) return;
        try{
          clearTimeout(state.savePositionTimeout);
          state.nodesPositionOrder = [];
          await ApiService.patch(`${mapsURL}${mapId}${elementsURL}`, data);
          dispatch('maps/mapCurrentSetUpdatedDate', new Date().toISOString(), {root: true}); // set updated date

          data.nodes.forEach(nodeData => {
            commit('UPDATE_NODE_ORIGINAL_GEOMETRY', {...nodeData, nodesOriginal});
          })

          const editsData = {
            type: SE_SYNC_TYPES.NODE_POSITION,
            data: data.nodes
          }
          dispatch('simultaneousEditing/syncEdits', editsData, {root: true});

          const message = data.nodes.length > 1 ? t('message.nodesPositionsSuccess') : t('message.nodePositionSuccess');
          await createToastFromStore(TOASTS_TYPES.SUCCESS, message, dispatch);
          data.nodes = [];
        } catch(err){
          restoreNodePosition({nodeIDs: nodesIdToSave, nodesOriginal, nodes, dispatch})

          getMapAreaService().renderFrame(); // update canvas
          // nodesIdToSave = [];
          clearTimeout(state.savePositionTimeout);
          const message = state.nodesPositionOrder.length > 1 ? t('message.nodesPositionsError') : t('message.nodePositionError');
          await createToastFromStore(TOASTS_TYPES.ERROR, message, dispatch);
          state.nodesPositionOrder = [];
          data.nodes = [];
        }

      }, 100) // 700
    },
    nodeMoveBy({state, getters, dispatch}, {nodeIds, dx, dy}){
      const nodes = getters.getNodes;
      const moved = [];
      nodeIds.forEach(id => {
        const node = nodes.find(node => node.id === parseInt(id));
        if(!node) return;
        node.x = Number((node.x + dx).toFixed(1));
        node.y = Number((node.y + dy).toFixed(1));
        updateEdgeRelated(node, dispatch);
        moved.push(node.id);
        if(!state.nudgedNodeIds.includes(node.id)) state.nudgedNodeIds.push(node.id);
      });
      if(!moved.length) return;
      getMapAreaService().renderFrame(); // reflect the move immediately
      // Coalesce a burst of nudges into one save: reset the timer on every move
      // so only the final position is persisted (one PATCH -> one MapChange).
      clearTimeout(state.nudgeSaveTimeout);
      state.nudgeSaveTimeout = setTimeout(() => {
        const ids = state.nudgedNodeIds;
        state.nudgedNodeIds = [];
        ids.forEach(id => dispatch('nodeSavePosition', id));
      }, NUDGE_SAVE_DEBOUNCE);
    },
    nodeUpdatePositionSE({getters, commit, dispatch}, nodesData){
      const nodesOriginal = getters.getRawNodes;
      const nodes = getters.getNodes;
      nodesData.forEach(data => {
        const id = data.id;
        const x = data.geometry.center.x;
        const y = data.geometry.center.y;

        const node = nodes.find(node => node.id === parseInt(id));
        node.x = x;
        node.y = y;

        commit('UPDATE_NODE_ORIGINAL_GEOMETRY', {...data, nodesOriginal});

        updateEdgeRelated(node, dispatch);
      })
      getMapAreaService().renderFrame(); // update canvas
    },
  }
}